import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { AdminService } from '../../../core/services/admin.service';
import { ToastService } from '../../../core/services/toast.service';
import { LanguageService } from '../../../core/services/language.service';
import { SubscriptionPlanDto, SubscriptionDiscountDto } from '../../../core/models/admin.models';
import {
  LucideAngularModule,
  CreditCard,
  CheckCircle,
  XCircle,
  RefreshCw,
  Plus,
  Edit,
  Trash2,
  X,
  Tag,
  ToggleLeft,
  ToggleRight,
  Percent,
  Archive,
  ArchiveRestore,
} from 'lucide-angular';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-subscription-plans',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, LucideAngularModule, TranslatePipe],
  templateUrl: './subscription-plans.component.html',
})
export class SubscriptionPlansComponent implements OnInit {
  private adminService = inject(AdminService);
  private toast = inject(ToastService);
  private lang = inject(LanguageService);
  private fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  fieldSuffix = computed(() => {
    const l = this.lang.currentLang();
    if (l === 'ru') return 'Ru';
    if (l === 'en') return 'En';
    if (l === 'uz-cyrl') return 'Cyrl';
    return '';
  });

  localized(plan: SubscriptionPlanDto, field: 'name' | 'description'): string | null {
    const suffix = this.fieldSuffix();
    if (!suffix) return plan[field] ?? null;
    const key = (field + suffix) as keyof SubscriptionPlanDto;
    return (plan[key] as string | null) || plan[field] || null;
  }

  plans = signal<SubscriptionPlanDto[]>([]);
  showArchived = signal(false);
  filteredPlans = computed(() =>
    this.showArchived() ? this.plans() : this.plans().filter((p) => !p.isArchived),
  );
  loading = signal(true);
  submitting = signal(false);
  errorMsg = signal('');

  // Plan dialog
  showPlanDialog = signal(false);
  editingPlan = signal<SubscriptionPlanDto | null>(null);

  // Discount dialog
  showDiscountDialog = signal(false);
  editingDiscount = signal<SubscriptionDiscountDto | null>(null);
  discountPlanId = signal<string>('');

  CreditCard = CreditCard;
  CheckCircle = CheckCircle;
  XCircle = XCircle;
  RefreshCw = RefreshCw;
  Plus = Plus;
  Edit = Edit;
  Trash2 = Trash2;
  X = X;
  Tag = Tag;
  ToggleLeft = ToggleLeft;
  ToggleRight = ToggleRight;
  Percent = Percent;
  Archive = Archive;
  ArchiveRestore = ArchiveRestore;

  planForm = this.fb.group({
    name: ['', [Validators.required, Validators.minLength(2)]],
    nameRu: [''],
    nameEn: [''],
    nameCyrl: [''],
    description: [''],
    descriptionRu: [''],
    descriptionEn: [''],
    descriptionCyrl: [''],
    maxStores: [1, [Validators.required, Validators.min(1)]],
    maxEmployees: [3, [Validators.required, Validators.min(1)]],
    maxProducts: [0, [Validators.required, Validators.min(0)]],
    monthlyPrice: [0, [Validators.required, Validators.min(0)]],
    displayOrder: [0],
    isView: [false],
    moduleAttendance: [false],
    moduleProductImages: [false],
  });

  discountForm = this.fb.group({
    description: ['', Validators.required],
    minMonths: [0, [Validators.required, Validators.min(0)]],
    discountPercent: [10, [Validators.required, Validators.min(1), Validators.max(99)]],
    isActive: [true],
  });

  ngOnInit() {
    this.load();
  }

  load() {
    this.loading.set(true);
    this.adminService
      .getAllSubscriptionPlans()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          if (res.success) this.plans.set(res.data);
          this.loading.set(false);
        },
        error: () => this.loading.set(false),
      });
  }

  // ----- Plan CRUD -----

  openCreatePlan() {
    this.editingPlan.set(null);
    this.planForm.reset({
      maxStores: 1,
      maxEmployees: 3,
      maxProducts: 0,
      monthlyPrice: 99000,
      displayOrder: 0,
      isView: false,
      moduleAttendance: false,
      moduleProductImages: false,
      nameRu: '',
      nameEn: '',
      nameCyrl: '',
      descriptionRu: '',
      descriptionEn: '',
      descriptionCyrl: '',
    });
    this.errorMsg.set('');
    this.showPlanDialog.set(true);
  }

  openEditPlan(plan: SubscriptionPlanDto) {
    this.editingPlan.set(plan);
    this.planForm.setValue({
      name: plan.name,
      nameRu: plan.nameRu ?? '',
      nameEn: plan.nameEn ?? '',
      nameCyrl: plan.nameCyrl ?? '',
      description: plan.description ?? '',
      descriptionRu: plan.descriptionRu ?? '',
      descriptionEn: plan.descriptionEn ?? '',
      descriptionCyrl: plan.descriptionCyrl ?? '',
      maxStores: plan.maxStores,
      maxEmployees: plan.maxEmployees,
      maxProducts: plan.maxProducts ?? 0,
      monthlyPrice: plan.monthlyPrice,
      displayOrder: plan.displayOrder,
      isView: plan.isView ?? false,
      moduleAttendance: plan.includedModules?.includes('attendance') ?? false,
      moduleProductImages: plan.includedModules?.includes('product_images') ?? false,
    });
    this.errorMsg.set('');
    this.showPlanDialog.set(true);
  }

  submitPlan() {
    if (this.planForm.invalid) return;
    this.submitting.set(true);
    this.errorMsg.set('');
    const v = this.planForm.value;
    const data = {
      name: v.name!,
      nameRu: v.nameRu || undefined,
      nameEn: v.nameEn || undefined,
      nameCyrl: v.nameCyrl || undefined,
      description: v.description || undefined,
      descriptionRu: v.descriptionRu || undefined,
      descriptionEn: v.descriptionEn || undefined,
      descriptionCyrl: v.descriptionCyrl || undefined,
      maxStores: v.maxStores!,
      maxEmployees: v.maxEmployees!,
      maxProducts: v.maxProducts ?? 0,
      monthlyPrice: v.monthlyPrice!,
      displayOrder: v.displayOrder ?? 0,
      isView: v.isView ?? false,
      includedModules: this.selectedModules(),
    };
    const finish = (success: boolean, msg: string) => {
      this.submitting.set(false);
      if (success !== false) {
        this.showPlanDialog.set(false);
        this.load();
      } else this.errorMsg.set(msg);
    };
    const onErr = (err: { error?: { message?: string } }) => {
      this.submitting.set(false);
      this.errorMsg.set(err?.error?.message ?? 'Xato');
    };

    if (this.editingPlan()) {
      this.adminService
        .updatePlan(this.editingPlan()!.id, data)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (r) => finish(r.success, r.message ?? r.errors?.join(' ') ?? ''),
          error: onErr,
        });
    } else {
      this.adminService
        .createPlan(data)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (r) => finish(r.success, r.message ?? r.errors?.join(' ') ?? ''),
          error: onErr,
        });
    }
  }

  togglePlan(plan: SubscriptionPlanDto) {
    this.adminService
      .togglePlan(plan.id, !plan.isActive)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toast.success(`"${plan.name}" holati o'zgartirildi`);
          this.load();
        },
        error: (err) => this.toast.error(err?.error?.message ?? 'Xato'),
      });
  }

  deletePlan(plan: SubscriptionPlanDto) {
    if (!confirm(`"${plan.name}" tarifini o'chirishni tasdiqlaysizmi?`)) return;
    this.adminService
      .deletePlan(plan.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toast.success(`"${plan.name}" o'chirildi`);
          this.load();
        },
        error: (err) => this.toast.error(err?.error?.message ?? "O'chirib bo'lmadi"),
      });
  }

  archivePlan(plan: SubscriptionPlanDto) {
    const action = plan.isArchived ? 'arxivdan chiqarish' : 'arxivlash';
    if (!confirm(`"${plan.name}" tarifini ${action}ni tasdiqlaysizmi?`)) return;
    this.adminService
      .archivePlan(plan.id, !plan.isArchived)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toast.success(
            `"${plan.name}" ${plan.isArchived ? 'arxivdan chiqarildi' : 'arxivlandi'}`,
          );
          this.load();
        },
        error: (err) => this.toast.error(err?.error?.message ?? 'Xato'),
      });
  }

  // ----- Discount CRUD -----

  openAddDiscount(planId: string) {
    this.discountPlanId.set(planId);
    this.editingDiscount.set(null);
    this.discountForm.reset({ minMonths: 0, discountPercent: 10, isActive: true });
    this.errorMsg.set('');
    this.showDiscountDialog.set(true);
  }

  openEditDiscount(planId: string, d: SubscriptionDiscountDto) {
    this.discountPlanId.set(planId);
    this.editingDiscount.set(d);
    this.discountForm.setValue({
      description: d.description,
      minMonths: d.minMonths,
      discountPercent: d.discountPercent,
      isActive: d.isActive,
    });
    this.errorMsg.set('');
    this.showDiscountDialog.set(true);
  }

  submitDiscount() {
    if (this.discountForm.invalid) return;
    this.submitting.set(true);
    this.errorMsg.set('');
    const v = this.discountForm.value;
    const data = {
      description: v.description!,
      minMonths: v.minMonths!,
      discountPercent: v.discountPercent!,
      isActive: v.isActive ?? true,
    };
    const finish = (success: boolean, msg: string) => {
      this.submitting.set(false);
      if (success !== false) {
        this.showDiscountDialog.set(false);
        this.load();
      } else this.errorMsg.set(msg);
    };
    const onErr = (err: { error?: { message?: string } }) => {
      this.submitting.set(false);
      this.errorMsg.set(err?.error?.message ?? 'Xato');
    };

    if (this.editingDiscount()) {
      this.adminService
        .updateDiscount(this.editingDiscount()!.id, data)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (r) => finish(r.success, r.message ?? r.errors?.join(' ') ?? ''),
          error: onErr,
        });
    } else {
      this.adminService
        .createDiscount(this.discountPlanId(), data)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (r) => finish(r.success, r.message ?? r.errors?.join(' ') ?? ''),
          error: onErr,
        });
    }
  }

  deleteDiscount(id: string) {
    if (!confirm("Chegirmani o'chirishni tasdiqlaysizmi?")) return;
    this.adminService
      .deleteDiscount(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: () => this.load() });
  }

  discountTypeLabel(minMonths: number): string {
    return minMonths === 0 ? 'Oddiy chegirma' : `${minMonths}+ oy shartida`;
  }

  selectedModules(): string[] {
    const modules: string[] = [];
    if (this.planForm.value.moduleAttendance) modules.push('attendance');
    if (this.planForm.value.moduleProductImages) modules.push('product_images');
    return modules;
  }

  hasModule(plan: SubscriptionPlanDto, module: string): boolean {
    return plan.includedModules?.includes(module) ?? false;
  }
}
